// Dev page (#/dev/canvas): every main screen laid out on a pan-and-zoom canvas, in the order you meet them, so the whole
// interface can be seen at once. The screens are the real components. They are drawn from the library and the fullest
// episode on the server (read only: nothing here saves, and the frames ignore the mouse).
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { startDraft } from "../../library/drafts";
import { preloadImages } from "../../library/images";
import { setLibrary, useLibrary } from "../../library/store";
import { isTimelineEpisode, resolve, type TimelineEpisode } from "../../model/timeline";
import { api } from "../api";
import { Phone } from "../common";
import { Home } from "../Home";
import { AssetPage, VoiceEditor } from "../library/AssetPage";
import { LibraryPage } from "../library/LibraryPage";
import { PAGES, type PageKind } from "../timeline/pages";
import { ScriptView } from "../timeline/ScriptView";
import { FullTimeline } from "../timeline/TimelineEditor";
import { TimelineProvider } from "../timeline/TimelineLoader";
import "../../index.css";
import "./canvas.css";

const none = () => {};
const FW = 515, FH = 1000, GX = 260, GY = 120; // a frame's size (title included) and the gaps between frames on the board

// A screen on the board (col, row are grid places) and an arrow between two of them, labelled with what the user does.
type Frame = { id: string; col: number; row: number; title: string; node: ReactNode };
type Edge = { from: string; to: string; label: string };

// The fullest episode, so the screens have something in them.
async function loadExample(): Promise<TimelineEpisode | null> {
  const list = await api.list();
  for (const s of [...list].sort((a, b) => b.lines - a.lines)) {
    const e = await api.getEpisode(s.id);
    if (isTimelineEpisode(e)) return e;
  }
  return null;
}

export default function Canvas() {
  const [ep, setEp] = useState<TimelineEpisode | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api.library().then(async lib => {
      setLibrary(lib);
      void preloadImages(lib).catch(none);
      setEp(await loadExample());
    }).catch(e => setError((e as Error).message));
  }, []);
  if (error) return <div className="cv-note">Couldn't load the example data: {error}</div>;
  if (ep === undefined) return <div className="cv-note">Loading the example project…</div>;
  if (ep === null) return <div className="cv-note">There is no timeline episode to draw from. Make one, then come back.</div>;
  return (
    <TimelineProvider value={{ ep, update: none, undo: none, redo: none, canUndo: false, canRedo: false }}>
      <Board ep={ep} />
    </TimelineProvider>
  );
}

function Board({ ep }: { ep: TimelineEpisode }) {
  const lib = useLibrary();
  const r = resolve(ep);
  const character = lib.assets.find(a => a.kind === "character" && a.picture?.src) ?? lib.assets.find(a => a.kind === "character");
  const line = r.lines.find(l => l.how !== "silent");
  const silence = r.lines.find(l => l.how === "silent" && !l.placeholder) ?? r.lines.find(l => l.how === "silent");
  const pic = r.pictures.find(p => p.motions?.length) ?? r.pictures[0];
  const feeling = r.feelings[0];
  const cam = r.camera[0];
  const char = line?.char ?? r.cast[0];

  const page = (kind: PageKind, props: { t?: number; editId?: string; char?: string; step?: string } = {}): ReactNode => {
    const P = PAGES[kind];
    return <P t={props.t ?? 0} editId={props.editId} char={props.char} step={props.step} onClose={none} onToast={none} onSeek={none} onOpen={none} />;
  };
  const place = lib.assets.find(a => a.kind === "background" && a.picture?.src);
  const prop = lib.assets.find(a => a.kind === "prop" && a.picture?.src);
  const [newCharacter] = useState(() => startDraft("character")); // a blank draft: opens on Picture
  const lineId = line?.id;
  const lineT = line?.start ?? 0;

  // Columns run left to right in the order the user takes the steps; rows hold the branches. Add and change screens of the
  // same thing share a column, one above the other, so they can be compared.
  const f: (Frame | null)[] = [
    { id: "home", col: 0, row: 3, title: "Home", node: <Home onQuick={none} onNew={none} onOpen={none} onLibrary={none} /> },
    { id: "script", col: 1, row: 3, title: "Script view", node: <ScriptView onBack={none} view="script" onView={none} /> },
    { id: "timeline", col: 1, row: 8, title: "Timeline view", node: <FullTimeline onBack={none} view="timeline" onView={none} /> },

    // Pictures: a new one opens on stage 1 and goes on with Next; an existing one opens on its Overview, which opens any stage
    { id: "addpic", col: 2, row: 0, title: "New picture: 1 Picture", node: page("picture", { t: r.display }) },
    pic ? { id: "picfaces", col: 3, row: 0, title: "A picture: 2 Faces", node: page("picture", { t: pic.t, editId: pic.id, step: "faces" }) } : null,
    pic ? { id: "picanimate", col: 4, row: 0, title: "A picture: 3 Animate", node: page("picture", { t: pic.t, editId: pic.id, step: "animate" }) } : null,
    pic ? { id: "picover", col: 2, row: 1, title: "A picture: Overview", node: page("picture", { t: pic.t, editId: pic.id }) } : null,
    pic ? { id: "picpicture", col: 3, row: 1, title: "A picture: 1 Picture", node: page("picture", { t: pic.t, editId: pic.id, step: "picture" }) } : null,

    // Lines: the same shape. A new one opens on Voice; an existing one on its Overview
    { id: "addline", col: 2, row: 5, title: "New line: 1 Voice", node: page("line", { t: r.display, char }) },
    lineId ? { id: "linefeel", col: 3, row: 5, title: "A line: 2 Expressions", node: page("line", { t: lineT, editId: lineId, char, step: "feel" }) } : null,
    lineId ? { id: "linecam", col: 4, row: 5, title: "A line: 3 Camera", node: page("line", { t: lineT, editId: lineId, char, step: "camera" }) } : null,
    lineId ? { id: "lineover", col: 2, row: 6, title: "A line: Overview", node: page("line", { t: lineT, editId: lineId, char }) } : null,
    lineId ? { id: "linevoice", col: 3, row: 6, title: "A line: 1 Voice", node: page("line", { t: lineT, editId: lineId, char, step: "voice" }) } : null,

    // A silent line is a line whose How is Silence: its Voice stage shows the length instead of the takes
    silence ? { id: "silencevoice", col: 3, row: 7, title: "A silence: 1 Voice", node: page("line", { t: silence.start, editId: silence.id, step: "voice" }) } : null,
    // Camera moves and feelings (added from the timeline view, changed from there too)
    { id: "addcam", col: 3, row: 9, title: "Add a camera move", node: page("camera", { t: r.display }) },
    cam ? { id: "chgcam", col: 3, row: 10, title: "Change a camera move", node: page("camera", { t: cam.t, editId: cam.id }) } : null,
    { id: "addfeel", col: 3, row: 11, title: "Add an expression", node: page("feeling", { t: r.display, char }) },
    feeling ? { id: "chgfeel", col: 3, row: 12, title: "Change an expression", node: page("feeling", { t: feeling.t, editId: feeling.id, char: feeling.char }) } : null,

    // The library
    { id: "lib", col: 1, row: 14, title: "Characters, places and props", node: <LibraryPage onBack={none} /> },
    // A character is an item with stages. An existing one opens on its Overview, a new one on Picture.
    character ? { id: "asset", col: 2, row: 14, title: "A character: Overview", node: <AssetPage assetId={character.id} onClose={none} /> } : null,
    character ? { id: "assetpic", col: 3, row: 14, title: "A character: 1 Picture", node: <AssetPage assetId={character.id} step="picture" onClose={none} /> } : null,
    character ? { id: "assetface", col: 4, row: 14, title: "A character: 2 Face", node: <AssetPage assetId={character.id} step="face" onClose={none} /> } : null,
    character ? { id: "assetvoice", col: 5, row: 14, title: "A character: 3 Voice", node: <AssetPage assetId={character.id} step="voice" onClose={none} /> } : null,
    { id: "newchar", col: 2, row: 15, title: "New character: 1 Picture", node: <AssetPage assetId={newCharacter} onClose={none} /> },
    character ? { id: "voice", col: 3, row: 15, title: "A character's voice, from a line", node: <VoiceEditor assetId={character.id} onBack={none} /> } : null,
    // A place or a prop has one stage, so one page and no stage bar.
    place ? { id: "place", col: 2, row: 16, title: "A place", node: <AssetPage assetId={place.id} onClose={none} /> } : null,
    prop ? { id: "prop", col: 2, row: 17, title: "A prop", node: <AssetPage assetId={prop.id} onClose={none} /> } : null,
  ];
  const frames = f.filter((x): x is Frame => !!x && !!x.node);
  const ids = new Set(frames.map(x => x.id));
  const edges: Edge[] = ([
    ["home", "script", "Tap an episode"], ["home", "lib", "Characters, places and props"],
    ["script", "addpic", "Image"], ["addpic", "picfaces", "Next: Faces"], ["picfaces", "picanimate", "Next: Animate"],
    ["script", "picover", "Tap a picture"], ["picover", "picpicture", "1 Picture"], ["picover", "picfaces", "2 Faces"], ["picover", "picanimate", "3 Animate"],
    ["script", "addline", "Audio"], ["addline", "linefeel", "Next: Expressions"], ["linefeel", "linecam", "Next: Camera"],
    ["script", "lineover", "Tap a line"], ["lineover", "linevoice", "1 Voice"], ["lineover", "linefeel", "2 Expressions"], ["lineover", "linecam", "3 Camera"],
    ["script", "silencevoice", "Tap a silence"], ["linevoice", "silencevoice", "How: Silence"], ["script", "timeline", "Timeline"],
    ["timeline", "addcam", "Camera"], ["timeline", "addfeel", "Expression"],
    ["timeline", "chgcam", "Select, then Edit"], ["timeline", "chgfeel", "Select, then Edit"],
    ["lib", "asset", "Tap a character"], ["asset", "assetpic", "1 Picture"], ["asset", "assetface", "2 Face"], ["asset", "assetvoice", "3 Voice"],
    ["lib", "newchar", "+ Character"], ["newchar", "assetface", "Next: Face"], ["assetface", "assetvoice", "Next: Voice"],
    ["lib", "place", "Tap a place"], ["lib", "prop", "Tap a prop"], ["linevoice", "voice", "Voice ›"],
  ] as const).filter(([a, b]) => ids.has(a) && ids.has(b)).map(([from, to, label]) => ({ from, to, label }));
  return <Viewport frames={frames} edges={edges} />;
}

// The pan-and-zoom surface. Drag or scroll to pan, pinch or ctrl/⌘-scroll to zoom, double-click to fit the screen.
function Viewport({ frames, edges }: { frames: Frame[]; edges: Edge[] }) {
  const view = useRef<HTMLDivElement>(null);
  const content = useRef<HTMLDivElement>(null);
  const [cam, setCam] = useState({ x: 0, y: 0, k: 0.2 });
  const at = new Map(frames.map(fr => [fr.id, { x: fr.col * (FW + GX), y: fr.row * (FH + GY) }]));
  const W = (Math.max(...frames.map(fr => fr.col)) + 1) * (FW + GX), H = (Math.max(...frames.map(fr => fr.row)) + 1) * (FH + GY);
  const fit = () => {
    const v = view.current!, c = content.current!;
    const k = Math.min(1, (v.clientWidth - 80) / c.offsetWidth, (v.clientHeight - 80) / c.offsetHeight);
    setCam({ k, x: (v.clientWidth - c.offsetWidth * k) / 2, y: 40 });
  };
  useLayoutEffect(fit, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const el = view.current!;
    const wheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect(), px = e.clientX - rect.left, py = e.clientY - rect.top;
      if (e.ctrlKey || e.metaKey) {
        setCam(c => {
          const k = Math.min(2, Math.max(0.03, c.k * Math.exp(-e.deltaY * 0.01)));
          return { k, x: px - ((px - c.x) / c.k) * k, y: py - ((py - c.y) / c.k) * k };
        });
      } else setCam(c => ({ ...c, x: c.x - e.deltaX, y: c.y - e.deltaY }));
    };
    el.addEventListener("wheel", wheel, { passive: false });
    return () => el.removeEventListener("wheel", wheel);
  }, []);

  const down = (e: React.PointerEvent) => {
    const x0 = e.clientX, y0 = e.clientY;
    let last = { x: x0, y: y0 };
    const move = (m: PointerEvent) => { const dx = m.clientX - last.x, dy = m.clientY - last.y; last = { x: m.clientX, y: m.clientY }; setCam(c => ({ ...c, x: c.x + dx, y: c.y + dy })); };
    const up = () => { removeEventListener("pointermove", move); removeEventListener("pointerup", up); };
    addEventListener("pointermove", move);
    addEventListener("pointerup", up);
  };

  return (
    <div ref={view} className="cv" onPointerDown={down} onDoubleClick={fit}>
      <div ref={content} className="cv-content" style={{ transform: `translate(${cam.x}px, ${cam.y}px) scale(${cam.k})` }}>
        <svg className="cv-edges" width={W} height={H}>
          <defs><marker id="cv-head" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto" markerUnits="userSpaceOnUse"><path d="M0,0 L8,4 L0,8 z" fill="currentColor" /></marker></defs>
          {edges.map(e => {
            const a = at.get(e.from)!, b = at.get(e.to)!;
            const x1 = a.x + FW, y1 = a.y + FH / 2, x2 = b.x, y2 = b.y + FH / 2, m = (x2 - x1) / 2;
            return <path key={e.from + e.to} d={`M${x1},${y1} C${x1 + m},${y1} ${x2 - m},${y2} ${x2},${y2}`} markerEnd="url(#cv-head)" />;
          })}
        </svg>
        {edges.map(e => {
          const a = at.get(e.from)!, b = at.get(e.to)!;
          return <span key={e.from + e.to} className="cv-label" style={{ left: (a.x + FW + b.x) / 2, top: (a.y + b.y) / 2 + FH / 2 }}>{e.label}</span>;
        })}
        {frames.map(fr => {
          const p = at.get(fr.id)!;
          return (
            <figure key={fr.id} className="cv-frame" style={{ left: p.x, top: p.y }}>
              <figcaption>{fr.title}</figcaption>
              <div className="cv-screen"><Phone>{fr.node}</Phone></div>
            </figure>
          );
        })}
      </div>
      <div className="cv-hud">{Math.round(cam.k * 100)}% · drag to pan · pinch or ⌘-scroll to zoom · double-click to fit</div>
    </div>
  );
}
